import { DownloadOutlined } from '@ant-design/icons';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import { type IReportWarehouseProductPhotoComparison } from '@mahawi/eshop-common/src/types';
import { Button, Divider, Space, Switch, Tooltip, Typography } from 'antd';
import {
  type ColumnGroupType,
  type ColumnsType,
  type ColumnType,
} from 'antd/es/table';
import React, { useContext, useEffect, useState } from 'react';
import { type Key } from 'react';
import { useSelector } from 'react-redux';

import { ENotificationSeverity } from '../../admin-types';
import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { notificationContext } from '../notification';
import { type RootState } from '../reducers';
import { downloaderDownloadAllPhotosWarehouseProduct } from '../reducers/downloader/reducer';
import { reportWarehouseProductPhotoComparisonLoad } from '../reducers/report/reducer';
import { SPACING } from '../theme/tokens';

interface DataType extends IReportWarehouseProductPhotoComparison {
  key: string;
  warehouseProductName: string;
  productName: string;
  difference: number;
}

function ReportWarehouseProductPhotoComparisonContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const reportWarehouseProductPhotoComparison = useSelector(
    (s: RootState) => s.Report.warehouseProductPhotoComparison,
  );
  const reportUpdatedAt = useSelector((s: RootState) => s.Report.updatedAt);
  const reportInProcess = useSelector((s: RootState) => s.Report.inProcess);
  const languageLanguageType = useSelector(
    (s: RootState) => s.Language.languageType,
  );

  const { openNotification } = useContext(notificationContext);
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnsType<DataType>>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [isActiveProducts, setIsActiveProducts] = useState<boolean>(true);

  useEffect((): void => {
    const dataSourceUE: DataType[] =
      reportWarehouseProductPhotoComparison?.map(
        (row: IReportWarehouseProductPhotoComparison): DataType => {
          const productName: string =
            getTranslation(row.product.names, languageLanguageType) ??
            row.product.uuid;

          return {
            key: row.warehouseProduct.uuid,
            ...row,
            warehouseProductName: row.warehouseProduct.name,
            productName,
            difference:
              row.product.photosCount - row.warehouseProduct.photoUrlsCount,
          };
        },
      ) ?? [];

    const columnsUE: ColumnsType<DataType> = [
      {
        title: 'Warehouse product',
        dataIndex: 'warehouseProductName',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.warehouseProduct.name, b.warehouseProduct.name),
        ...getColumnSearch<DataType>('warehouseProductName'),
        render: (_: string, record: DataType): React.ReactElement => (
          <Link
            to={`/downloader/warehouse-product/${record.warehouseProduct.uuid}`}
            label={record.warehouseProduct.name}
            openInNewTab
          />
        ),
        width: '35%',
      },
      {
        title: 'Product',
        dataIndex: 'productName',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.productName, b.productName),
        ...getColumnSearch<DataType>('productName'),
        render: (_: string, record: DataType): React.ReactElement => (
          <Link
            to={`/product/${record.product.uuid}`}
            label={record.productName}
            openInNewTab
            additionalSearchParams={{ photo: 'open' }}
          />
        ),
        width: '35%',
      },
      {
        title: 'Warehouse product photos',
        dataIndex: ['warehouseProduct', 'photoUrlsCount'],
        sorter: (a: DataType, b: DataType): number =>
          a.warehouseProduct.photoUrlsCount - b.warehouseProduct.photoUrlsCount,
        align: 'right',
        width: '7%',
      },
      {
        title: 'Product photos',
        dataIndex: ['product', 'photosCount'],
        sorter: (a: DataType, b: DataType): number =>
          a.product.photosCount - b.product.photosCount,
        align: 'right',
        width: '7%',
      },
      {
        title: 'Difference',
        dataIndex: 'difference',
        sorter: (a: DataType, b: DataType): number =>
          a.difference - b.difference,
        filters: [
          { text: 'Less photos than Warehouse product', value: 'lessThanWP' },
          { text: 'More photos than Warehouse product', value: 'moreThanWP' },
        ],
        onFilter: (value: boolean | Key, record: DataType): boolean => {
          if (value === 'lessThanWP') {
            return record.difference < 0;
          }
          if (value === 'moreThanWP') {
            return record.difference > 0;
          }
          return false;
        },
        align: 'right',
        render: (value: number): React.ReactElement => (
          <Typography.Text strong type={value < 0 ? 'danger' : 'warning'}>
            {value > 0 ? `+${value}` : value}
          </Typography.Text>
        ),
        width: '8%',
      },
      {
        title: 'Actions',
        filterSearch: true,
        filters: [
          { text: 'Less photos than Warehouse product', value: 'lessThanWP' },
          { text: 'More photos than Warehouse product', value: 'moreThanWP' },
        ],
        onFilter: (value: boolean | Key, record: DataType): boolean => {
          if (value === 'lessThanWP') {
            return (
              record.product.photosCount <
              record.warehouseProduct.photoUrlsCount
            );
          }
          if (value === 'moreThanWP') {
            return (
              record.product.photosCount >
              record.warehouseProduct.photoUrlsCount
            );
          }
          return false;
        },
        render: (_: unknown, record: DataType): React.ReactElement => (
          <Tooltip title="Download all photos">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={(): void => {
                dispatch(
                  downloaderDownloadAllPhotosWarehouseProduct(
                    record.warehouseProduct.uuid,
                  ),
                );
                openNotification('topRight', {
                  severity: ENotificationSeverity.INFO,
                  title: 'Download started',
                  description: `Downloading photos for ${record.warehouseProduct.name}`,
                });
              }}
            >
              Download all photos
            </Button>
          </Tooltip>
        ),
        width: '8%',
      },
    ];

    setDataSource(dataSourceUE);
    setColumns(columnsUE);
  }, [
    reportWarehouseProductPhotoComparison,
    reportUpdatedAt,
    languageLanguageType,
    openNotification,
  ]);

  return (
    <>
      <DetailPageHeader
        title="Product photos"
        onReload={(): void => {
          dispatch(reportWarehouseProductPhotoComparisonLoad(isActiveProducts));
        }}
        isReloadDisabled={reportInProcess}
        isReloadHighlighted={!reportWarehouseProductPhotoComparison}
      />

      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Switch
          checkedChildren="Active products"
          unCheckedChildren="Disabled products"
          checked={isActiveProducts}
          onChange={(checked: boolean): void => {
            setIsActiveProducts(checked);
          }}
        />

        <Tooltip
          title={
            selectedRowKeys.length === 0
              ? 'Select rows to batch download'
              : `Download photos for ${selectedRowKeys.length} selected products`
          }
        >
          <Button
            icon={<DownloadOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={(): void => {
              selectedRowKeys.forEach((key: Key): void => {
                dispatch(
                  downloaderDownloadAllPhotosWarehouseProduct(key as string),
                );
              });
              openNotification('topRight', {
                severity: ENotificationSeverity.INFO,
                title: 'Batch download started',
                description: `Downloading photos for ${selectedRowKeys.length} products`,
              });
              setSelectedRowKeys([]);
            }}
          >
            Batch download ({selectedRowKeys.length})
          </Button>
        </Tooltip>

        <UpdatedAt value={reportUpdatedAt} />
      </Space>

      <Divider />

      <Table
        columns={columns as (ColumnGroupType<object> | ColumnType<object>)[]}
        dataSource={dataSource}
        isLoading={reportInProcess}
        emptyText="No photo comparison data found"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: (keys: Key[]): void => setSelectedRowKeys(keys),
        }}
      />
    </>
  );
}

export default ReportWarehouseProductPhotoComparisonContainer;
